import React, { useCallback } from 'react';
import { Image, Platform, View } from 'react-native';
import Text from 'design-system/Text';
import { AppView, Button, ButtonGroup, StatusBar, rem } from 'design-system';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { KYCDocumentType } from '../types';
import EnlargeDocumentButton from '../components/EnlargeDocumentButton';
import useUploadPOI from '../hooks/useUploadPOI';
import { isWeb } from '../../../constants';
const ConfirmDocumentScreen = ({ navigation, route }) => {
    const flow = route.params?.flow;
    const photoFiles = route.params?.files;
    const documentType = route.params?.documentType;
    const styles = useStyles(styleSet);
    const containerStyles = useMarginBottom('paddingBottom', undefined, styles.flex);
    const { width: frameRealWidth, deviceWidth } = useAppFrameDimensions(isWeb);
    const frameWidth = Platform.OS === 'ios' && Platform.isPad ? frameRealWidth : deviceWidth;
    const [onUpload] = useUploadPOI(flow, documentType);
    const handleContinue = useCallback(() => {
        switch (documentType) {
            case KYCDocumentType.NATIONAL_ID:
            case KYCDocumentType.DRIVING_LICENCE:
            case KYCDocumentType.UK_BIOMETRIC_RESIDENCE_PERMIT: {
                if (photoFiles.length === 1) {
                    navigation.navigateDeprecated('WealthBackDocumentSideUploadInstructionsScreen', {
                        flow,
                        documentType,
                        files: photoFiles,
                    });
                }
                else {
                    onUpload(photoFiles);
                }
                break;
            }
            case KYCDocumentType.PASSPORT: {
                onUpload(photoFiles);
                break;
            }
            default:
                navigation.popToTop();
                break;
        }
    }, [documentType, flow, onUpload, photoFiles]);
    const handleRetake = useCallback(() => {
        navigation.pop();
    }, []);
    const isPassport = documentType === KYCDocumentType.PASSPORT;
    return (<AppView avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <View style={containerStyles}>
        <View style={[styles.mainContainer, isPassport && styles.passportMainContainer]}>
          <View style={[
            { width: frameWidth - 2 * rem(isPassport ? 84 : 55) },
            isPassport ? styles.passportImgContainer : styles.imgContainer,
        ]}>
            <Image source={{ uri: photoFiles[0].path }} style={(isPassport ? styles.passportImg : styles.img)} resizeMode="cover"/>
            <View style={styles.enlargeBtnContainer}>
              <EnlargeDocumentButton source={{ uri: photoFiles[0].path }}/>
            </View>
          </View>
        </View>
        <View style={styles.textContainer}>
          <Text Text-16 centered>
            Make sure you have uploaded the entire document page and the details are clear to read with no blur or glare
          </Text>
        </View>
        <ButtonGroup>
          <Button brandReversed title="Retake" onPress={handleRetake}/>
          <Button brand title="Submit document" onPress={handleContinue}/>
        </ButtonGroup>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    mainContainer: {
        flex: 3.5,
        alignItems: 'center',
        justifyContent: 'center',
    },
    passportMainContainer: {
        flex: 5.5,
        alignItems: 'center',
        justifyContent: 'center',
    },
    textContainer: {
        flex: 2,
        paddingHorizontal: rem(16),
    },
    imgContainer: {
        overflow: 'hidden',
        borderRadius: rem(16),
        aspectRatio: 1.6,
        backgroundColor: colors.cards.onDark,
    },
    passportImgContainer: {
        overflow: 'hidden',
        borderRadius: rem(16),
        aspectRatio: 0.69,
    },
    img: {
        width: '100%',
        height: '200%',
        transform: [
            {
                translateY: rem(-50),
            },
        ],
    },
    passportImg: {
        width: '100%',
        height: '100%',
        transform: [
            {
                scale: 1.4,
            },
            {
                translateY: rem(20),
            },
        ],
    },
    enlargeBtnContainer: {
        left: 0,
        right: 0,
        bottom: rem(8),
        position: 'absolute',
        alignItems: 'center',
    },
}));
export default ConfirmDocumentScreen;
